// Settings rows grouped Membership → Supervisors → Time tracking, each group a titled card (§9.4).
import { SETTING_GROUP_LABELS, SETTING_GROUP_ORDER, type SettingGroup } from '@shared/settings-catalogue';
import type { ReactNode } from 'react';
import { SectionTitle } from '@/components/SectionTitle';
import { Card } from '@/components/ui/card';
import type { SettingModeValue } from '@/types/settings';
import type { GroupedItem } from './setting-items';
import { SettingRow, type SettingRowContext } from './SettingRow';

export interface SettingGroupsProps {
  items: GroupedItem[];
  context: SettingRowContext;
  onChange: (key: string, value: unknown) => void;
  onModeChange?: (key: string, mode: SettingModeValue, value: unknown) => void;
  onReset?: (key: string) => void;
  pendingKey?: string | null;
  /** Replaces a group's rows (e.g. the Membership line on a project, which lives on the Members tab). */
  groupOverride?: Partial<Record<SettingGroup, ReactNode>>;
}

export function SettingGroups({ items, context, onChange, onModeChange, onReset, pendingKey = null, groupOverride = {} }: SettingGroupsProps): JSX.Element {
  return (
    <div className="space-y-6">
      {SETTING_GROUP_ORDER.map((group) => {
        const rows = items.filter((i) => i.group === group);
        const override = groupOverride[group];
        if (rows.length === 0 && override === undefined) return null;
        const headingId = `settings-group-${group}`;
        return (
          <section key={group} aria-labelledby={headingId}>
            <SectionTitle id={headingId} title={SETTING_GROUP_LABELS[group]} />
            <Card className="py-0">
              {override !== undefined
                ? override
                : rows.map(({ item }) => (
                    <SettingRow
                      key={item.key}
                      item={item}
                      context={context}
                      pending={pendingKey === item.key}
                      onChange={(value) => onChange(item.key, value)}
                      onModeChange={onModeChange ? (mode) => onModeChange(item.key, mode, item.value) : undefined}
                      onReset={onReset ? () => onReset(item.key) : undefined}
                    />
                  ))}
            </Card>
          </section>
        );
      })}
    </div>
  );
}
